import React, { useContext, useEffect, useState } from 'react';
import { ethers } from 'ethers';
import { Web3Context } from '../../context/Web3Context';
import useContract from '../../hooks/useContract';
import { formatUSDT, shortenAddress } from '../../utils/formatters';
import Loader from '../../components/Loader/Loader';
import './Admin.css';

const Admin = () => {
  const { account, isOwner } = useContext(Web3Context);
  const { adminCall, getContractBalance, getMissingPool, getPoolAccumulations, getPoolEligibleCounts, getTotalUsers, getUserDetails, getUserPackages, getIncomeBreakdown, getPoolEligibilities, getReferrals, loading } = useContract();
  const [contractBal, setContractBal] = useState('0');
  const [missingPool, setMissingPool] = useState('0');
  const [accumulations, setAccumulations] = useState(null);
  const [eligCounts, setEligCounts] = useState(null);
  const [totalUsers, setTotalUsers] = useState(0);
  const [pageLoading, setPageLoading] = useState(true);

  // Form states
  const [regAddr, setRegAddr] = useState('');
  const [regRef, setRegRef] = useState('');
  const [upgUser, setUpgUser] = useState('');
  const [upgPkg, setUpgPkg] = useState('');
  const [retUser, setRetUser] = useState('');
  const [retPkg, setRetPkg] = useState('');
  const [poolType, setPoolType] = useState('1');
  const [poolAmt, setPoolAmt] = useState('');
  const [resetPoolType, setResetPoolType] = useState('0');
  const [withdrawAmt, setWithdrawAmt] = useState('');
  const [feePercent, setFeePercent] = useState('');
  const [newAdmin, setNewAdmin] = useState('');
  const [newOwner, setNewOwner] = useState('');

  // Lookup states
  const [lookupId, setLookupId] = useState('');
  const [lookupData, setLookupData] = useState(null);
  const [lookupLoading, setLookupLoading] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setPageLoading(true);
      try {
        const [bal, miss, accum, elig, users] = await Promise.all([
          getContractBalance(), getMissingPool(), getPoolAccumulations(), getPoolEligibleCounts(), getTotalUsers(),
        ]);
        setContractBal(bal);
        setMissingPool(miss);
        setAccumulations(accum);
        setEligCounts(elig);
        setTotalUsers(users);
      } catch (err) { console.error(err); }
      finally { setPageLoading(false); }
    };
    fetchData();
  }, [account]);

  const refreshStats = async () => {
    try {
      const [bal, miss, accum, elig, users] = await Promise.all([
        getContractBalance(), getMissingPool(), getPoolAccumulations(), getPoolEligibleCounts(), getTotalUsers(),
      ]);
      setContractBal(bal);
      setMissingPool(miss);
      setAccumulations(accum);
      setEligCounts(elig);
      setTotalUsers(users);
    } catch {}
  };

  const handleLookup = async () => {
    if (!lookupId) return;
    setLookupLoading(true);
    setLookupData(null);
    try {
      const uid = parseInt(lookupId);
      const [details, pkgs, income, elig, refs] = await Promise.all([
        getUserDetails(uid),
        getUserPackages(uid),
        getIncomeBreakdown(uid),
        getPoolEligibilities(uid),
        getReferrals(uid),
      ]);
      setLookupData({ details, pkgs, income, elig, refs });
    } catch (err) {
      setLookupData(null);
      console.error(err);
    } finally {
      setLookupLoading(false);
    }
  };

  const handleOp = async (op, a1, a2) => {
    try {
      await adminCall(op, a1, a2);
      await refreshStats();
    } catch {}
  };

  if (pageLoading) return <Loader text="Loading Admin" />;

  const poolNames = ['Gold', 'Platinum', 'Diamond', 'Crown', 'Blue', 'Ambassador'];
  const poolKeys = ['gold', 'platinum', 'diamond', 'crown', 'blue', 'ambassador'];
  const selectedPoolIdx = parseInt(poolType) - 1;
  const selectedPoolAccum = accumulations?.[poolKeys[selectedPoolIdx]] || '0';
  const selectedPoolElig = eligCounts?.[poolKeys[selectedPoolIdx]] || 0;

  return (
    <div className="admin-page">
      <div className="page-header"><h1 className="page-title">Admin Panel</h1></div>

      {/* Stats */}
      <div className="admin-stats">
        <div className="as-item"><span className="as-value">{totalUsers}</span><span className="as-label">Total Users</span></div>
        <div className="as-item"><span className="as-value">{formatUSDT(contractBal)}</span><span className="as-label">Contract Balance</span></div>
        <div className="as-item"><span className="as-value">{formatUSDT(missingPool)}</span><span className="as-label">Missing Pool</span></div>
      </div>

      {/* User Lookup */}
      <div className="dash-card">
        <h3 className="card-title">User Lookup</h3>
        <div className="admin-form">
          <div className="lookup-row">
            <input className="form-input" placeholder="Enter User ID" value={lookupId} onChange={(e) => setLookupId(e.target.value)} type="number" />
            <button className="btn-admin" disabled={lookupLoading || !lookupId} onClick={handleLookup}>
              {lookupLoading ? 'Loading...' : 'Search'}
            </button>
          </div>
        </div>
        {lookupData && lookupData.details && (
          <div className="lookup-result">
            <div className="lu-section">
              <div className="lu-row"><span className="lu-label">User ID</span><span className="lu-val highlight">{lookupData.details.id}</span></div>
              <div className="lu-row"><span className="lu-label">Wallet</span><span className="lu-val mono">{shortenAddress(lookupData.details.account)}</span></div>
              <div className="lu-row"><span className="lu-label">Upline ID</span><span className="lu-val">{lookupData.details.referrer}</span></div>
              <div className="lu-row"><span className="lu-label">Status</span><span className={`lu-val ${lookupData.details.isActive ? 'green' : 'red'}`}>{lookupData.details.isActive ? 'Active' : 'Inactive'}</span></div>
              <div className="lu-row"><span className="lu-label">Direct Referrals</span><span className="lu-val">{lookupData.details.totalDirectReferrals}</span></div>
            </div>
            <div className="lu-section">
              <h4 className="lu-section-title">Income</h4>
              <div className="lu-row"><span className="lu-label">Total Income</span><span className="lu-val highlight">{formatUSDT(lookupData.details.totalIncome)} USDT</span></div>
              <div className="lu-row"><span className="lu-label">Claimable</span><span className="lu-val green">{formatUSDT(lookupData.details.claimableAmount)} USDT</span></div>
              <div className="lu-row"><span className="lu-label">Total Claimed</span><span className="lu-val">{formatUSDT(lookupData.details.totalClaimed)} USDT</span></div>
              <div className="lu-row"><span className="lu-label">Sponsor Income</span><span className="lu-val">{formatUSDT(lookupData.income?.sponsorIncome)} USDT</span></div>
              <div className="lu-row"><span className="lu-label">Level Income</span><span className="lu-val">{formatUSDT(lookupData.income?.levelIncome)} USDT</span></div>
              <div className="lu-row"><span className="lu-label">Gold Pool</span><span className="lu-val">{formatUSDT(lookupData.income?.goldPoolIncome)} USDT</span></div>
              <div className="lu-row"><span className="lu-label">Platinum Pool</span><span className="lu-val">{formatUSDT(lookupData.income?.platinumPoolIncome)} USDT</span></div>
              <div className="lu-row"><span className="lu-label">Diamond Pool</span><span className="lu-val">{formatUSDT(lookupData.income?.diamondPoolIncome)} USDT</span></div>
              <div className="lu-row"><span className="lu-label">Crown Pool</span><span className="lu-val">{formatUSDT(lookupData.income?.crownPoolIncome)} USDT</span></div>
              <div className="lu-row"><span className="lu-label">Blue Pool</span><span className="lu-val">{formatUSDT(lookupData.income?.bluePoolIncome)} USDT</span></div>
              <div className="lu-row"><span className="lu-label">Ambassador Pool</span><span className="lu-val">{formatUSDT(lookupData.income?.ambassadorPoolIncome)} USDT</span></div>
            </div>
            <div className="lu-section">
              <h4 className="lu-section-title">Packages</h4>
              <div className="lu-packages">
                {['Starter', 'Bronze', 'Silver', 'Gold', 'Platinum', 'Diamond'].map((name, i) => (
                  <div key={i} className={`lu-pkg ${lookupData.pkgs[i] ? 'active' : ''}`}>
                    <span className="lu-pkg-name">{name}</span>
                    <span className="lu-pkg-status">{lookupData.pkgs[i] ? 'Active' : 'Locked'}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="lu-section">
              <h4 className="lu-section-title">Pool Eligibility</h4>
              <div className="lu-elig-grid">
                {poolNames.map((name, i) => {
                  const keys = ['gold', 'platinum', 'diamond', 'crown', 'blue', 'ambassador'];
                  const isElig = lookupData.elig?.[keys[i]] || false;
                  return (
                    <div key={i} className={`lu-elig-item ${isElig ? 'yes' : ''}`}>
                      <span className={`lu-elig-dot ${isElig ? 'active' : ''}`}></span>
                      <span>{name}</span>
                    </div>
                  );
                })}
              </div>
            </div>
            {lookupData.refs && lookupData.refs.length > 0 && (
              <div className="lu-section">
                <h4 className="lu-section-title">Direct Referrals ({lookupData.refs.length})</h4>
                <div className="lu-refs">{lookupData.refs.map((r) => <span key={r} className="lu-ref-chip">#{r}</span>)}</div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Pool Stats */}
      <div className="dash-card">
        <h3 className="card-title">Pool Accumulations</h3>
        <div className="pool-stats-grid">
          {poolNames.map((name, i) => (
            <div key={i} className="ps-item">
              <span className="ps-name">{name}</span>
              <span className="ps-val">{formatUSDT(accumulations?.[poolKeys[i]] || '0')}</span>
              <span className="ps-elig">{eligCounts?.[poolKeys[i]] || 0} eligible</span>
            </div>
          ))}
        </div>
      </div>

      {/* Admin Register */}
      <div className="dash-card">
        <h3 className="card-title">Register User (No USDT)</h3>
        <div className="admin-form">
          <input className="form-input" placeholder="User address" value={regAddr} onChange={(e) => setRegAddr(e.target.value)} />
          <input className="form-input" placeholder="Referrer ID (optional)" value={regRef} onChange={(e) => setRegRef(e.target.value)} />
          <button className="btn-admin" disabled={loading || !regAddr} onClick={() => handleOp(1, ethers.BigNumber.from(regAddr).toString(), parseInt(regRef) || 0)}>
            Register
          </button>
        </div>
      </div>

      {/* Admin Upgrade */}
      <div className="dash-card">
        <h3 className="card-title">Upgrade User (No USDT)</h3>
        <div className="admin-form">
          <input className="form-input" placeholder="User ID" value={upgUser} onChange={(e) => setUpgUser(e.target.value)} />
          <input className="form-input" placeholder="Package ID (2-6)" value={upgPkg} onChange={(e) => setUpgPkg(e.target.value)} />
          <button className="btn-admin" disabled={loading || !upgUser || !upgPkg} onClick={() => handleOp(2, parseInt(upgUser), parseInt(upgPkg))}>
            Upgrade
          </button>
        </div>
      </div>

      {/* Admin Retopup - Owner only */}
        <div className="dash-card">
          <h3 className="card-title">Retopup User (No USDT)</h3>
          <div className="admin-form">
            <input className="form-input" placeholder="User ID" value={retUser} onChange={(e) => setRetUser(e.target.value)} />
            <input className="form-input" placeholder="Package ID (1-6)" value={retPkg} onChange={(e) => setRetPkg(e.target.value)} />
            <button className="btn-admin" disabled={loading || !retUser || !retPkg} onClick={() => handleOp(3, parseInt(retUser), parseInt(retPkg))}>
              Retopup
            </button>
          </div>
        </div>
      

      {/* Set Pool Round - with stats */}
      <div className="dash-card">
        <h3 className="card-title">Set Pool Round Income</h3>
        <div className="admin-form">
          <select className="form-input" value={poolType} onChange={(e) => setPoolType(e.target.value)}>
            {poolNames.map((name, i) => (<option key={i} value={i + 1}>{name}</option>))}
          </select>
          <div className="pool-round-stats">
            <div className="prs-item">
              <span className="prs-label">Eligible Users</span>
              <span className="prs-value">{selectedPoolElig}</span>
            </div>
            <div className="prs-item">
              <span className="prs-label">Accumulated</span>
              <span className="prs-value">{formatUSDT(selectedPoolAccum)} USDT</span>
            </div>
            <div className="prs-item">
              <span className="prs-label">Per User</span>
              <span className="prs-value">
                {selectedPoolElig > 0
                  ? formatUSDT(String(parseFloat(selectedPoolAccum) / selectedPoolElig))
                  : '0.00'} USDT
              </span>
            </div>
          </div>
          <input className="form-input" placeholder="Amount per user (USDT)" value={poolAmt} onChange={(e) => setPoolAmt(e.target.value)} />
          <button className="btn-admin" disabled={loading || !poolAmt} onClick={() => handleOp(4, parseInt(poolType), ethers.utils.parseEther(poolAmt).toString())}>
            Set Round
          </button>
        </div>
      </div>

      {/* Reset Pool Accumulation */}
      <div className="dash-card">
        <h3 className="card-title">Reset Pool Accumulation</h3>
        <div className="admin-form">
          <select className="form-input" value={resetPoolType} onChange={(e) => setResetPoolType(e.target.value)}>
            <option value="0">All Pools</option>
            {poolNames.map((name, i) => (<option key={i} value={i + 1}>{name}</option>))}
          </select>
          <button className="btn-admin" disabled={loading} onClick={() => handleOp(5, parseInt(resetPoolType), 0)}>
            Reset
          </button>
        </div>
      </div>

      {/* Claim Missing Pool */}
      <div className="dash-card">
        <h3 className="card-title">Claim Missing Pool ({formatUSDT(missingPool)} USDT)</h3>
        <button className="btn-admin" disabled={loading || parseFloat(missingPool) === 0} onClick={() => handleOp(6, 0, 0)}>
          Claim Missing Pool
        </button>
      </div>

      {/* Emergency Withdraw */}
      <div className="dash-card">
        <h3 className="card-title">Emergency Withdraw</h3>
        <div className="admin-form">
          <input className="form-input" placeholder="Amount (USDT)" value={withdrawAmt} onChange={(e) => setWithdrawAmt(e.target.value)} />
          <button className="btn-admin btn-danger" disabled={loading || !withdrawAmt} onClick={() => handleOp(7, ethers.utils.parseEther(withdrawAmt).toString(), 0)}>
            Withdraw
          </button>
        </div>
      </div>

      {/* Set Withdraw Fee */}
      <div className="dash-card">
        <h3 className="card-title">Set Withdraw Fee %</h3>
        <div className="admin-form">
          <input className="form-input" placeholder="Fee percent (0-30)" value={feePercent} onChange={(e) => setFeePercent(e.target.value)} />
          <button className="btn-admin" disabled={loading || !feePercent} onClick={() => handleOp(8, parseInt(feePercent), 0)}>
            Set Fee
          </button>
        </div>
      </div>

      {/* Update Admin - Owner only */}
      {isOwner && (
        <div className="dash-card">
          <h3 className="card-title">Update Admin Address</h3>
          <div className="admin-form">
            <input className="form-input" placeholder="New admin address" value={newAdmin} onChange={(e) => setNewAdmin(e.target.value)} />
            <button className="btn-admin" disabled={loading || !newAdmin} onClick={() => handleOp(9, ethers.BigNumber.from(newAdmin).toString(), 0)}>
              Update Admin
            </button>
          </div>
        </div>
      )}

      {/* Transfer Ownership - Owner only */}
      {isOwner && (
        <div className="dash-card">
          <h3 className="card-title">Transfer Ownership</h3>
          <div className="admin-form">
            <input className="form-input" placeholder="New owner address" value={newOwner} onChange={(e) => setNewOwner(e.target.value)} />
            <button className="btn-admin btn-danger" disabled={loading || !newOwner} onClick={() => handleOp(10, ethers.BigNumber.from(newOwner).toString(), 0)}>
              Transfer
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Admin;
